import { Box } from '@mui/material';
import { useStyles } from './InteractiveTreatmentPlan.styles';
import { Button, btnType, Input, inputType, inputSize, btnSize } from '../../../../../index';
import { Icons } from '../../../../../icons';
import { useState } from 'react';

/**
 * Step 2 of Interactive Treatment Plan Flow
 * Displays AI-generated goals and allows adding/editing/selecting goals
 */
const InteractiveGoalsStep = ({
  goals = [],
  onAddGoal,
  onEditGoal,
  onToggleGoalSelection,
  onViewReasoning,
}) => {
  const classes = useStyles();
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalDetails, setNewGoalDetails] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalName, setEditingGoalName] = useState('');
  const [editingGoalDetails, setEditingGoalDetails] = useState('');

  const handleAddGoal = () => {
    if (newGoalName.trim() && newGoalDetails.trim()) {
      onAddGoal({
        name: newGoalName.trim(),
        details: newGoalDetails.trim(),
      });
      setNewGoalName('');
      setNewGoalDetails('');
      setIsAddingNew(false);
    }
  };

  const handleStartEdit = (goal) => {
    setEditingGoalId(goal.id);
    setEditingGoalName(goal.name);
    setEditingGoalDetails(goal.details || '');
  };

  const handleSaveEdit = (goalId) => {
    if (editingGoalName.trim()) {
      onEditGoal({ 
        id: goalId, 
        name: editingGoalName.trim(),
        details: editingGoalDetails.trim(),
      });
      setEditingGoalId(null);
      setEditingGoalName('');
      setEditingGoalDetails('');
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Box className={classes.container}>
        <Box className={classes.sectionContainer}>
          <Box className={classes.sectionHeader}>
            <Box className={classes.sectionTitle}>Goals</Box>
            {goals.some(g => g.rationale) && (
              <Box
                className={classes.viewReasoningLink}
                onClick={onViewReasoning}
              >
                View the reasoning
              </Box>
            )}
          </Box>

          {/* Add Goal Input */}
          <Box display="flex" flexDirection="column" gap="12px" padding="24px">
            <Box display="flex" gap="12px">
              <Input
                fullWidth
                value={newGoalName}
                onChange={(e) => {
                  setNewGoalName(e.target.value);
                  if (!isAddingNew && e.target.value.trim()) {
                    setIsAddingNew(true);
                  }
                }}
                placeholder="Add your own goal here"
                variant={inputType.DEFAULT}
                size={inputSize.M}
              />
              <Button
                variant={btnType.OUTLINE}
                startIcon={<Icons glyph="plus" />}
                size={btnSize.SMALL}
                onClick={handleAddGoal}
                className={classes.outlinedBtn}
                // disabled={!newGoalName.trim() || !newGoalDetails.trim()}
              >
                Add Goal
              </Button>
            </Box>
            {isAddingNew && (
              <Input
                fullWidth
                multiline
                rows={2}
                value={newGoalDetails}
                onChange={(e) => setNewGoalDetails(e.target.value)}
                placeholder="Goal details"
                variant={inputType.DEFAULT}
                size={inputSize.M}
              />
            )}
          </Box>
        </Box>
      </Box>
      {/* Goals List */}
      <Box display="flex" flexDirection="column" gap="24px">
        {goals.map(goal => {
          goal.id = goal._id || goal.id
          return goal;
        }).map((goal, index) => (
          <Box
            key={goal.id || index}
            className={
              goal.preSelected
                ? classes.goalCardSelected
                : classes.goalCard
            }
          >
            {editingGoalId === goal.id ? (
              // Editing mode - show name + details inputs with save button
              <Box display="flex" flexDirection="column" gap="20px" width="100%">
                <Box display="flex" flexDirection="column" gap="8px">
                  <Box className={classes.objectivesLabel}>Goal</Box>
                  <Input
                    fullWidth
                    multiline
                    rows={2}
                    value={editingGoalName}
                    onChange={(e) => setEditingGoalName(e.target.value)}
                    variant={inputType.DEFAULT}
                    size={inputSize.M}
                    autoFocus
                  />
                </Box>
                <Box display="flex" flexDirection="column" gap="8px">
                  <Box className={classes.objectivesLabel}>Details</Box>
                  <Input
                    fullWidth
                    multiline
                    rows={3}
                    value={editingGoalDetails}
                    onChange={(e) => setEditingGoalDetails(e.target.value)}
                    placeholder="Goal details (optional)"
                    variant={inputType.DEFAULT}
                    size={inputSize.M}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant={btnType.OUTLINE}
                    startIcon={<Icons glyph="save-outlined" />}
                    size={btnSize.SMALL}
                    onClick={() => handleSaveEdit(goal.id)}
                    className={classes.outlinedBtn}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              // Display mode
              <>
                <Box className={classes.goalCardContent}>
                  <Box className={classes.goalName}>{goal.name}</Box>
                  {goal.details && (
                    <Box className={classes.goalDetails}>{goal.details}</Box>
                  )}
                  {goal.expectedCompletion && (
                    <Box className={classes.expectedCompletion}>
                      Expected completion: {goal.expectedCompletion}
                    </Box>
                  )}
                </Box>

                <Box className={classes.goalCardActions}>
                  <Button
                    variant={btnType.OUTLINE}
                    startIcon={<Icons glyph="edit-underline" />}
                    size={btnSize.SMALL}
                    onClick={() => handleStartEdit(goal)}
                    className={classes.outlinedBtn}
                  >
                    Edit
                  </Button>
                  <Button
                    size={btnSize.SMALL}
                    variant={
                      goal.preSelected
                        ? btnType.PRIMARY
                        : btnType.OUTLINE
                    }
                    onClick={() => onToggleGoalSelection(goal.id || index)}
                    className={goal.preSelected ? classes.primaryBtn : classes.outlinedBtn}
                  >
                    {goal.preSelected ? 'Selected' : 'Select Goal'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { InteractiveGoalsStep };

